/**
 * Buildings BAU pathways: stock-based electrification model.
 * Produces thermsByYear, kwhByYear, rngShareByYear for use as baseline in Policy Tool.
 * Aligns with CARB-style assumptions (sales electrification, turnover, RNG).
 *
 * Sales electrification curves: all scenarios use LINEAR ramps (share = linear in time to target).
 * Choice: linear for transparency and consistency with policy year targets; a logistic/S-curve
 * could be added later for smoother adoption if desired.
 */

import {
  BuildingsEnergyData,
  calculateTotalNaturalGasConsumption,
  calculateTotalElectricityConsumption,
} from './buildingsEnergyDataService';

export type BuildingsBauScenario = 'conservative' | 'current_policy' | 'accelerated';

export interface BuildingsBauPathway {
  years: number[];
  thermsByYear: number[];
  kwhByYear: number[];
  rngShareByYear: number[];
  stockElectrifiedByYear?: number[];
  stockFossilByYear?: number[];
  salesElectrificationShareByYear?: number[];
}

/** Annual turnover rate: share of stock replaced each year (e.g. 6%). */
export const BUILDINGS_TURNOVER_RATE = 0.06;

/** Heat pump coefficient of performance (consistent across codebase). */
export const HEAT_PUMP_COP = 3.5;

/** kWh per therm (thermal). */
export const KWH_PER_THERM = 29.3;

const NG_EMISSION_FACTOR_KG_PER_THERM = 5.29;

/**
 * Initial electrified stock share in the base year (2023).
 * Used as the starting point for stock evolution: stockElectrified[baseYear] = this value.
 * Assumption: small share of building heating/cooking stock is already electric in 2023 (~5%).
 * Can be refined with data (e.g. from EIA or utility mix) if available.
 */
export const INITIAL_STOCK_ELECTRIFIED_BASE_YEAR = 0.05;

/** RNG share (0–1) in 2045 by scenario. Used in UI and emissions. */
export const RNG_SHARE_2045_BY_SCENARIO: Record<BuildingsBauScenario, number> = {
  conservative: 0.1,
  current_policy: 0.3,
  accelerated: 0.32,
};

/**
 * Baseline therms = no-electrification counterfactual (first-order approximation).
 * We use OBSERVED natural gas consumption from buildings data as the fossil demand series.
 * This is an approximation: observed data may already reflect some electrification, so true
 * "no electrification" therms could be higher. Refinement (e.g. sector-level or survey-based
 * counterfactual) may be done later.
 */
function getBaselineThermsNoElectrification(
  rawBuildingsData: BuildingsEnergyData,
  years: number[]
): number[] {
  return years.map((y) => {
    const v = calculateTotalNaturalGasConsumption(rawBuildingsData, y);
    return Number.isFinite(v) && v > 0 ? v : 0;
  });
}

/**
 * Baseline building electricity before adding electrification load (first-order approximation).
 * We use OBSERVED electricity consumption as the non-electrification trend (plug load, cooling, etc.);
 * electrification adder is added separately in the model.
 * This is an approximation: observed kWh may already include some heat-pump load. May be refined later.
 */
function getBaselineTrendKwh(
  rawBuildingsData: BuildingsEnergyData,
  years: number[]
): number[] {
  return years.map((y) => {
    const v = calculateTotalElectricityConsumption(rawBuildingsData, y);
    return Number.isFinite(v) && v > 0 ? v : 0;
  });
}

/**
 * Sales electrification share (0–1) by year. All ramps are LINEAR in time (see module doc).
 * - Conservative: does not reach 100%; caps at 50% by 2045 (slower ramp).
 * - Current policy: blended curve — residential 100% electric sales by 2035, commercial by 2045.
 *   Aggregate uses 50/50 blend so blended share reaches 1 by 2045 with earlier weight from res.
 * - Accelerated: 100% electric sales by 2035 (faster than current policy).
 */
function getSalesElectrificationShareByYear(
  scenario: BuildingsBauScenario,
  years: number[],
  baseYear: number,
  _targetYear: number
): number[] {
  const result: number[] = [];
  for (const y of years) {
    if (y <= baseYear) {
      result.push(0);
      continue;
    }
    switch (scenario) {
      case 'conservative': {
        const endYear = 2045;
        const t = Math.min(1, (y - baseYear) / (endYear - baseYear));
        result.push(t * 0.5);
        break;
      }
      case 'current_policy': {
        // Blended: residential 100% by 2035, commercial 100% by 2045 (50/50 weight)
        const resShare = y >= 2035 ? 1 : Math.min(1, (y - baseYear) / (2035 - baseYear));
        const comShare = Math.min(1, (y - baseYear) / (2045 - baseYear));
        const blended = 0.5 * resShare + 0.5 * comShare;
        result.push(blended);
        break;
      }
      case 'accelerated': {
        const endYear = 2035;
        const t = Math.min(1, (y - baseYear) / (endYear - baseYear));
        result.push(t);
        break;
      }
      default:
        result.push(0);
    }
  }
  return result;
}

/**
 * RNG share (0–1) by year. Uses RNG_SHARE_2045_BY_SCENARIO for 2045 target; linear ramp from base year.
 */
function getRngShareByYear(
  scenario: BuildingsBauScenario,
  years: number[],
  baseYear: number,
  targetYear: number
): number[] {
  const result: number[] = [];
  const rng2045 = RNG_SHARE_2045_BY_SCENARIO[scenario];
  for (const y of years) {
    if (y <= baseYear) {
      result.push(0);
      continue;
    }
    const t = Math.min(1, (y - baseYear) / (targetYear - baseYear));
    result.push(t * rng2045);
  }
  return result;
}

/**
 * Stock evolution (correct formula with fossil-remaining term):
 * stockElectrified[t] = stockElectrified[t-1] + turnoverRate * (1 - stockElectrified[t-1]) * salesElectrificationShare[t]
 * Clamp to [0, 1].
 */
function evolveStock(
  salesShareByYear: number[],
  turnoverRate: number,
  initialStockElectrified: number,
  years: number[],
  baseYear: number
): { stockElectrifiedByYear: number[]; stockFossilByYear: number[] } {
  const baseIdx = years.indexOf(baseYear);
  const stockElectrifiedByYear: number[] = years.map(() => 0);
  const stockFossilByYear: number[] = years.map(() => 1);

  for (let i = 0; i < years.length; i++) {
    if (i <= baseIdx) {
      stockElectrifiedByYear[i] = initialStockElectrified;
      stockFossilByYear[i] = 1 - initialStockElectrified;
      continue;
    }
    const prev = Math.max(0, Math.min(1, stockElectrifiedByYear[i - 1]));
    const salesShare = Math.max(0, Math.min(1, salesShareByYear[i] ?? 0));
    const delta =
      turnoverRate * (1 - prev) * salesShare;
    let next = prev + delta;
    next = Math.max(0, Math.min(1, next));
    stockElectrifiedByYear[i] = next;
    stockFossilByYear[i] = 1 - next;
  }
  return { stockElectrifiedByYear, stockFossilByYear };
}

/**
 * Build BAU pathway from raw buildings data and scenario.
 * Uses baseline therms/kWh from raw data; stock evolution drives fossil share and electrification adder.
 */
export function getBuildingsBauPathway(
  rawBuildingsData: BuildingsEnergyData,
  scenario: BuildingsBauScenario,
  options?: {
    baseYear?: number;
    targetYear?: number;
    turnoverRate?: number;
    initialStockElectrified?: number;
    cop?: number;
  }
): BuildingsBauPathway {
  const baseYear = options?.baseYear ?? 2023;
  const targetYear = options?.targetYear ?? 2045;
  const turnoverRate = options?.turnoverRate ?? BUILDINGS_TURNOVER_RATE;
  const initialStockElectrified = options?.initialStockElectrified ?? INITIAL_STOCK_ELECTRIFIED_BASE_YEAR;
  const cop = options?.cop ?? HEAT_PUMP_COP;

  const years = Array.from(
    { length: targetYear - 2005 + 1 },
    (_, i) => 2005 + i
  );

  const baselineTherms = getBaselineThermsNoElectrification(rawBuildingsData, years);
  const baselineKwh = getBaselineTrendKwh(rawBuildingsData, years);

  // For years beyond data, hold last value (e.g. 2023) constant
  const lastTherms = baselineTherms[baselineTherms.length - 1] ?? baselineTherms[years.indexOf(2023)] ?? 0;
  const lastKwh = baselineKwh[baselineKwh.length - 1] ?? baselineKwh[years.indexOf(2023)] ?? 0;
  for (let i = 0; i < years.length; i++) {
    if (baselineTherms[i] === 0 && years[i] > 2023) baselineTherms[i] = lastTherms;
    if (baselineKwh[i] === 0 && years[i] > 2023) baselineKwh[i] = lastKwh;
  }

  const salesShareByYear = getSalesElectrificationShareByYear(
    scenario,
    years,
    baseYear,
    targetYear
  );
  const { stockElectrifiedByYear, stockFossilByYear } = evolveStock(
    salesShareByYear,
    turnoverRate,
    initialStockElectrified,
    years,
    baseYear
  );

  const thermsByYear = years.map((_, i) => baselineTherms[i] * stockFossilByYear[i]);
  const thermsDisplacedByYear = years.map(
    (_, i) => baselineTherms[i] * stockElectrifiedByYear[i]
  );
  const addedKwhByYear = thermsDisplacedByYear.map(
    (td) => td * (KWH_PER_THERM / cop)
  );
  const kwhByYear = years.map((_, i) => baselineKwh[i] + addedKwhByYear[i]);

  const rngShareByYear = getRngShareByYear(scenario, years, baseYear, targetYear);

  // Validation: stock electrified monotonic and <= 1
  for (let i = 1; i < stockElectrifiedByYear.length; i++) {
    if (stockElectrifiedByYear[i] < stockElectrifiedByYear[i - 1] - 1e-6) {
      console.warn('Buildings BAU: stockElectrified decreased at year', years[i]);
    }
    if (stockElectrifiedByYear[i] > 1.0001) {
      console.warn('Buildings BAU: stockElectrified > 1 at year', years[i]);
    }
  }

  return {
    years,
    thermsByYear,
    kwhByYear,
    rngShareByYear,
    stockElectrifiedByYear,
    stockFossilByYear,
    salesElectrificationShareByYear: salesShareByYear,
  };
}

/**
 * Get therms for a given year from pathway (interpolate if needed).
 */
export function getPathwayTherms(pathway: BuildingsBauPathway, year: number): number {
  const i = pathway.years.indexOf(year);
  if (i >= 0) return pathway.thermsByYear[i];
  if (year <= pathway.years[0]) return pathway.thermsByYear[0];
  if (year >= pathway.years[pathway.years.length - 1])
    return pathway.thermsByYear[pathway.thermsByYear.length - 1];
  const iHi = pathway.years.findIndex((y) => y >= year);
  const iLo = iHi - 1;
  if (iLo < 0) return pathway.thermsByYear[0];
  const t = (year - pathway.years[iLo]) / (pathway.years[iHi] - pathway.years[iLo]);
  return pathway.thermsByYear[iLo] + t * (pathway.thermsByYear[iHi] - pathway.thermsByYear[iLo]);
}

/**
 * Get kWh for a given year from pathway.
 */
export function getPathwayKwh(pathway: BuildingsBauPathway, year: number): number {
  const i = pathway.years.indexOf(year);
  if (i >= 0) return pathway.kwhByYear[i];
  if (year <= pathway.years[0]) return pathway.kwhByYear[0];
  if (year >= pathway.years[pathway.years.length - 1])
    return pathway.kwhByYear[pathway.kwhByYear.length - 1];
  const iHi = pathway.years.findIndex((y) => y >= year);
  const iLo = iHi - 1;
  if (iLo < 0) return pathway.kwhByYear[0];
  const t = (year - pathway.years[iLo]) / (pathway.years[iHi] - pathway.years[iLo]);
  return pathway.kwhByYear[iLo] + t * (pathway.kwhByYear[iHi] - pathway.kwhByYear[iLo]);
}

/**
 * Get RNG share for a given year (0–1).
 */
export function getPathwayRngShare(pathway: BuildingsBauPathway, year: number): number {
  const i = pathway.years.indexOf(year);
  if (i >= 0) return pathway.rngShareByYear[i];
  if (year <= pathway.years[0]) return pathway.rngShareByYear[0];
  if (year >= pathway.years[pathway.years.length - 1])
    return pathway.rngShareByYear[pathway.rngShareByYear.length - 1];
  const iHi = pathway.years.findIndex((y) => y >= year);
  const iLo = iHi - 1;
  if (iLo < 0) return pathway.rngShareByYear[0];
  const t = (year - pathway.years[iLo]) / (pathway.years[iHi] - pathway.years[iLo]);
  return pathway.rngShareByYear[iLo] + t * (pathway.rngShareByYear[iHi] - pathway.rngShareByYear[iLo]);
}

/**
 * Flat pathway: 2023 energy scaled by population only (no state or local policy).
 * Used for cost views "BAU costs" and "Total costs" (cost vs flat baseline).
 */
export function getFlatBuildingsPathway(baseline: {
  buildingsBauPathway?: BuildingsBauPathway;
  populationData: Record<number, number>;
}): BuildingsBauPathway {
  const years: number[] = [];
  for (let y = 2023; y <= 2045; y++) years.push(y);
  const pop = baseline.populationData;
  const pop2023 = pop[2023] ?? Object.values(pop)[0] ?? 1;
  const pathway = baseline.buildingsBauPathway;
  const therms2023 = pathway ? getPathwayTherms(pathway, 2023) : 0;
  const kwh2023 = pathway ? getPathwayKwh(pathway, 2023) : 0;
  const thermsByYear = years.map((y) => therms2023 * ((pop[y] ?? pop2023) / pop2023));
  const kwhByYear = years.map((y) => kwh2023 * ((pop[y] ?? pop2023) / pop2023));
  const rngShareByYear = years.map(() => 0);
  return { years, thermsByYear, kwhByYear, rngShareByYear };
}

/**
 * Get electrified stock share for a given year (0–1). Used for display (e.g. "Electrified share in 2045 under BAU").
 */
export function getPathwayStockElectrified(pathway: BuildingsBauPathway, year: number): number {
  const arr = pathway.stockElectrifiedByYear;
  if (!arr || arr.length === 0) return pathway.years?.length ? 0 : 0;
  const i = pathway.years.indexOf(year);
  if (i >= 0) return arr[i];
  if (year <= pathway.years[0]) return arr[0];
  if (year >= pathway.years[pathway.years.length - 1]) return arr[arr.length - 1];
  const iHi = pathway.years.findIndex((y) => y >= year);
  const iLo = iHi - 1;
  if (iLo < 0) return arr[0];
  const t = (year - pathway.years[iLo]) / (pathway.years[iHi] - pathway.years[iLo]);
  return arr[iLo] + t * (arr[iHi] - arr[iLo]);
}

export { NG_EMISSION_FACTOR_KG_PER_THERM };

/** One row of the Berkeley (or any location) BAU debug table */
export interface BuildingsBauDebugRow {
  scenario: BuildingsBauScenario | 'current_policy_slider_0' | 'current_policy_slider_100';
  stockElectrified2023: number;
  stockElectrified2030: number;
  stockElectrified2045: number;
  therms2045: number;
  kwh2045: number;
  rngShare2045: number;
  buildingEmissions2045MT: number;
}

/**
 * Build the Berkeley (or any location) BAU debug table.
 * Returns one row per BAU scenario plus current_policy with slider 0 and slider 100.
 * gPerKwh2045: grid carbon intensity in 2045 (g CO2e/kWh). Use 0 for clean grid.
 */
export function getBuildingsBauDebugTable(
  rawBuildingsData: BuildingsEnergyData,
  options?: { gPerKwh2045?: number }
): BuildingsBauDebugRow[] {
  const gPerKwh = options?.gPerKwh2045 ?? 0;
  const scenarios: BuildingsBauScenario[] = ['conservative', 'current_policy', 'accelerated'];
  const rows: BuildingsBauDebugRow[] = [];

  for (const scenario of scenarios) {
    const pathway = getBuildingsBauPathway(rawBuildingsData, scenario);
    const i2023 = pathway.years.indexOf(2023);
    const i2030 = pathway.years.indexOf(2030);
    const i2045 = pathway.years.indexOf(2045);
    const stock2023 = pathway.stockElectrifiedByYear?.[i2023] ?? INITIAL_STOCK_ELECTRIFIED_BASE_YEAR;
    const stock2030 = pathway.stockElectrifiedByYear?.[i2030] ?? 0;
    const stock2045 = pathway.stockElectrifiedByYear?.[i2045] ?? 0;
    const therms2045 = pathway.thermsByYear[i2045] ?? 0;
    const kwh2045 = pathway.kwhByYear[i2045] ?? 0;
    const rng2045 = pathway.rngShareByYear[i2045] ?? 0;
    const ngMT = (therms2045 * (1 - rng2045) * NG_EMISSION_FACTOR_KG_PER_THERM) / 1000;
    const elecMT = (kwh2045 * gPerKwh) / 1_000_000;
    rows.push({
      scenario,
      stockElectrified2023: stock2023,
      stockElectrified2030: stock2030,
      stockElectrified2045: stock2045,
      therms2045,
      kwh2045,
      rngShare2045: rng2045,
      buildingEmissions2045MT: ngMT + elecMT,
    });
  }

  const pathwayCP = getBuildingsBauPathway(rawBuildingsData, 'current_policy');
  const i2045 = pathwayCP.years.indexOf(2045);
  const bauTherms = pathwayCP.thermsByYear[i2045] ?? 0;
  const bauKwh = pathwayCP.kwhByYear[i2045] ?? 0;
  const bauRng = pathwayCP.rngShareByYear[i2045] ?? 0;
  const bauNgMT = (bauTherms * (1 - bauRng) * NG_EMISSION_FACTOR_KG_PER_THERM) / 1000;
  const bauElecMT = (bauKwh * gPerKwh) / 1_000_000;
  rows.push({
    scenario: 'current_policy_slider_0',
    stockElectrified2023: pathwayCP.stockElectrifiedByYear?.[pathwayCP.years.indexOf(2023)] ?? INITIAL_STOCK_ELECTRIFIED_BASE_YEAR,
    stockElectrified2030: pathwayCP.stockElectrifiedByYear?.[pathwayCP.years.indexOf(2030)] ?? 0,
    stockElectrified2045: pathwayCP.stockElectrifiedByYear?.[i2045] ?? 0,
    therms2045: bauTherms,
    kwh2045: bauKwh,
    rngShare2045: bauRng,
    buildingEmissions2045MT: bauNgMT + bauElecMT,
  });
  const addedKwh100 = bauTherms * (KWH_PER_THERM / HEAT_PUMP_COP);
  const totalKwh100 = bauKwh + addedKwh100;
  const elecMT100 = (totalKwh100 * gPerKwh) / 1_000_000;
  rows.push({
    scenario: 'current_policy_slider_100',
    stockElectrified2023: pathwayCP.stockElectrifiedByYear?.[pathwayCP.years.indexOf(2023)] ?? INITIAL_STOCK_ELECTRIFIED_BASE_YEAR,
    stockElectrified2030: pathwayCP.stockElectrifiedByYear?.[pathwayCP.years.indexOf(2030)] ?? 0,
    stockElectrified2045: pathwayCP.stockElectrifiedByYear?.[i2045] ?? 0,
    therms2045: 0,
    kwh2045: totalKwh100,
    rngShare2045: bauRng,
    buildingEmissions2045MT: 0 + elecMT100,
  });
  return rows;
}
